import Report from "./Report";
import { IRequest, IResponse } from "./RequestResponse";
import { GameID } from "./Game";
import { CompID } from "./Competency";
import SortType from "./Sort";
//import { SiteID } from "./Site";

export interface Entry {
    _id?: string;
    timestamp: number;
    userId: string;
    userName: string;
    gameId: GameID;
    attempt: number;
    score: number;
    report: Report;
    siteId: string;
    divId: string;
    compId: CompID;
}

export type EntryKey = keyof Entry;

const EntryKeys: EntryKey[] = [
    'timestamp',
    'userId',
    'userName',
    'gameId',
    'attempt',
    'score',
    'report',
    '_id'
];

export interface EntryFilter {
    siteIDs?: string[];
    divIDs?: string[];
    compIDs?: CompID[];
    gameIDs?: GameID[];
    fromDate?: number;
    toDate?: number;
    userIDContains?: string[];
    userNameContains?: string[];
}

export enum EntrySort {
    Score,
    GameID,
    SiteID,
    UserID,
    UserName,
    Timestamp,
    Report,
    Attempt,
    DivID,
    CompID,
}

export type EntrySorts = [EntrySort, SortType][];

export interface EntryScoreGroup {
    min: number;
    max: number;
}

export interface EntryFilterAndSort {
    filter: EntryFilter,
    sorts: EntrySorts
}

export interface EntryGetRequest extends EntryFilterAndSort, IRequest {
    limit: number;
    page: number;
    scoreGroup: EntryScoreGroup[];
    limitTopRecords: number;
}

export interface EntrySetRequest extends IRequest {
    userId: string,
    compId: CompID,
    gameId: GameID,
    score: number,
    userName: string,
    siteId: string,
    divId: string,
}

export interface EntryUserScore {
    score: number;
    userName: string;
    userId: string;
    siteId: string;
    divId: string;
}

export interface EntryUserGameScore {
    score: number;
    gameId: GameID;
    userName: string;
    userId: string;
    siteId: string;
    divId: string;
}

export interface EntryUserAttempt {
    attempt: number;
    userName: string;
    userId: string;
    siteId: string;
    divId: string;
}

export interface EntryGameScore {
    score: number;
    gameId: GameID;
}

export interface EntryGameAttempt {
    attempt: number;
    gameId: GameID;
}

export interface EntrySiteAttempt {
    attempt: number;
    siteId: string;
}

export interface EntryTopRecords {
    userBestScores?: EntryUserScore[];
    userGameBestScores?: EntryUserGameScore[];
    userMostAttempts?: EntryUserAttempt[];
    gameBestAvgScores?: EntryGameScore[];
    gameMostAttempts?: EntryGameAttempt[];
};

export interface EntryGetResponse extends IResponse<{
    entries: Entry[];
    page: number;
    pages: number;
    chart: {
        averageScores: [GameID, number][],
        groupedScores: [EntryScoreGroup, number][],
        monthlyAttempts: [number, number][],
        monthlyUsers: [number, number][],
        siteAttempts: [string, number][];
    },
    topRecords: EntryTopRecords
}> { }

export interface EntryExportRequest extends EntryFilterAndSort, IRequest { }

export type EntryFilterValidation = { [T in keyof EntryFilter]?: string };

const EntrySortFnTimestamp = (isAsc: boolean) => {
    return (entry1: Entry, entry2: Entry) =>
        entry1.timestamp == entry2.timestamp ? 0 :
            (entry1.timestamp > entry2.timestamp ? (isAsc ? 1 : -1) : (isAsc ? -1 : 1));
}

const EntrySortFnScore = (isAsc: boolean) => {
    return (entry1: Entry, entry2: Entry) =>
        entry1.score == entry2.score ? 0 :
            (entry1.score > entry2.score ? (isAsc ? 1 : -1) : (isAsc ? -1 : 1));
}

const EntrySortFnGameID = (isAsc: boolean) => {
    return (entry1: Entry, entry2: Entry) =>
        entry1.gameId == entry2.gameId ? 0 :
            (entry1.gameId > entry2.gameId ? (isAsc ? 1 : -1) : (isAsc ? -1 : 1));
}

const EntrySortFnUserName = (isAsc: boolean) => {
    return (entry1: Entry, entry2: Entry) =>
        entry1.userName == entry2.userName ? 0 :
            (entry1.userName > entry2.userName ? (isAsc ? 1 : -1) : (isAsc ? -1 : 1));
}

const EntrySortFnUserID = (isAsc: boolean) => {
    return (entry1: Entry, entry2: Entry) =>
        entry1.userId == entry2.userId ? 0 :
            (entry1.userId > entry2.userId ? (isAsc ? 1 : -1) : (isAsc ? -1 : 1));
}

const EntrySortFnAttempt = (isAsc: boolean) => {
    return (entry1: Entry, entry2: Entry) => (entry1.attempt - entry2.attempt) * (isAsc ? 1 : -1);
}

export const EntrySortFn = (sorts: [EntrySort, SortType][]) => {
    var sortTypes: boolean[] = [];
    var sortProps: EntrySort[] = [];
    /*Let's separate property name from ascendant or descendant keyword*/
    for (var i = 0; i < sorts.length; i++) {
        sortTypes[sortTypes.length] = sorts[i][1] == SortType.Ascending;
        sortProps[sortProps.length] = sorts[i][0];
    }
    return (entry1: Entry, entry2: Entry) => {
        var i = 0, result = 0, numberOfProperties = sortTypes.length;
        /*Cycle on values until find a difference!*/
        while (result === 0 && i < numberOfProperties) {
            if (sortProps[i] == EntrySort.Timestamp) {
                result = EntrySortFnTimestamp(sortTypes[i])(entry1, entry2);
            }
            if (sortProps[i] == EntrySort.GameID) {
                result = EntrySortFnGameID(sortTypes[i])(entry1, entry2);
            }
            if (sortProps[i] == EntrySort.UserID) {
                result = EntrySortFnUserID(sortTypes[i])(entry1, entry2);
            }
            if (sortProps[i] == EntrySort.UserName) {
                result = EntrySortFnUserName(sortTypes[i])(entry1, entry2);
            }
            if (sortProps[i] == EntrySort.Score) {
                result = EntrySortFnScore(sortTypes[i])(entry1, entry2);
            }
            if (sortProps[i] == EntrySort.Attempt) {
                result = EntrySortFnAttempt(sortTypes[i])(entry1, entry2);
            }
            i++;
        }
        return result;
    }
};

export default Entry;